import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { MobileDialog } from "../components/mobile";
import { isRemovedFromActiveDiary } from "../lib/lessonLifecycle";
import { useSafeBack } from "../hooks/useSafeBack";
import { useVisibilityRefetch } from "../hooks/useVisibilityRefetch";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Clock, MapPin, Calendar, Phone, Mail,
  CreditCard, DollarSign, Check, MessageSquare, Pencil, X, Handshake, Play, ChevronRight,
} from "lucide-react";
import { DarkModeToggle } from "../components/DarkModeToggle";
import { formatApiError } from "../utils/apiError";
import { londonYMD } from "../utils/timezone";
import StageConfirmationModal from "../components/instructor/StageConfirmationModal";
import WorkModeAIReviewSection from "../components/instructor/WorkModeAIReviewSection";
import VideoEvidenceReview from "../components/instructor/VideoEvidenceReview";
import StruggleFollowUpTile from "../components/instructor/StruggleFollowUpTile";
import SinceYouLastOpenedCard from "../components/instructor/SinceYouLastOpenedCard";
import PupilMemoryCard from "../components/instructor/PupilMemoryCard";
import PupilRhythmReviewCard from "../components/instructor/PupilRhythmReviewCard";
import InstructorConversation from "../components/instructor/InstructorConversation";
import PupilUnderstandingQueue from "../components/instructor/PupilUnderstandingQueue";
// Surface-2 Truth Hardening (Feb 2026): payment-truth helper. The
// "Paid" badge MUST be gated on the authoritative `paid_at` field,
// not the raw `payment_status` enum.
import { paymentBadgeKey } from "../lib/paymentTruth";
import PupilFinancialHistory from "../components/payments/PupilFinancialHistory";

const API = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_BADGE = {
  paid: { bg: "bg-emerald-500", text: "Paid", icon: Check },
  unpaid: { bg: "bg-pink-500", text: "Unpaid", icon: DollarSign },
  payment_due: { bg: "bg-amber-500", text: "Payment due", icon: DollarSign },
  part_paid: { bg: "bg-amber-500", text: "Part", icon: DollarSign },
  credit_used: { bg: "bg-violet-500", text: "Credit", icon: CreditCard },
};

export default function PupilProfile() {
  const { pupilId } = useParams();
  // Route-parameter changes must reset the whole profile. Old identity/drafts
  // and late reads must never be displayed beside another pupil's actions.
  return <PupilProfileContent key={pupilId} />;
}

function PupilProfileContent() {
  const { pupilId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "";

  const goBack = useSafeBack();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [transferModal, setTransferModal] = useState(false);
  const [transferForm, setTransferForm] = useState({
    reason: "better_location",
    new_postcode: "",
    note_to_learner: "",
  });
  const [transferring, setTransferring] = useState(false);
  const [stageConfOpen, setStageConfOpen] = useState(false);
  const [stageConfirmations, setStageConfirmations] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);

  const fetchStageConfirmations = useCallback(async () => {
    if (!pupilId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/learner-framework/pupils/${pupilId}/stage-confirmations`,
        { withCredentials: true }
      );
      setStageConfirmations(Array.isArray(data) ? data : []);
    } catch {
      // Non-blocking — older pupils may have none yet.
    }
  }, [pupilId]);

  useEffect(() => { fetchStageConfirmations(); }, [fetchStageConfirmations]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/api/lesson-events/history/${pupilId}`, { withCredentials: true });
      setData(res);
      setEditForm({
        phone: res.pupil.phone || "",
        email: res.pupil.email || "",
        default_lesson_price: res.pupil.default_lesson_price || "",
        hours_credit: res.pupil.hours_credit || "",
        notes: res.pupil.notes || "",
        address_1: res.pupil.address_1 || res.pupil.pickup_location || "",
        home_postcode: res.pupil.home_postcode || res.pupil.postcode || "",
      });
    } catch { toast.error("Failed to load pupil"); }
    setLoading(false);
  }, [pupilId]);

  useEffect(() => { if (user?.id) fetchHistory(); }, [fetchHistory, user]);

  const refreshPupil = useCallback(() => {
    if (!user?.id) return;
    fetchHistory();
    fetchStageConfirmations();
  }, [fetchHistory, fetchStageConfirmations, user?.id]);
  useVisibilityRefetch(refreshPupil, {
    enabled: Boolean(user?.id) && !editing && !transferModal && !stageConfOpen,
    marker: "pupil-profile",
  });

  const savePupil = async () => {
    try {
      await axios.put(`${API}/api/pupils/${pupilId}`, editForm, { withCredentials: true });
      toast.success("Pupil updated");
      setEditing(false);
      fetchHistory();
    } catch { toast.error("Failed"); }
  };

  const submitTransfer = async () => {
    setTransferring(true);
    try {
      await axios.post(
        `${API}/api/transfer-network/instructor-initiated`,
        {
          pupil_id: pupilId,
          reason: transferForm.reason,
          new_postcode: transferForm.new_postcode || null,
          note_to_learner: transferForm.note_to_learner || null,
        },
        { withCredentials: true },
      );
      toast.success("Suggestion sent — the learner will see it and approve or decline");
      setTransferModal(false);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't send — check the details"));
    } finally {
      setTransferring(false);
    }
  };

  if (loading) return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
    </div>
  );

  if (!data) return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center">
      <p className="text-slate-400">Pupil not found</p>
    </div>
  );

  const { pupil, lessons, summary, next_lesson, lesson_readiness } = data;
  // Connection truth is separate from the pupil's teaching/lifecycle status.
  // Only a completed invite redemption represents an active NDG connection;
  // registration completion is already gated by mandatory terms/privacy acceptance.
  const inviteStatus = (pupil.invite_status || "none").toLowerCase();
  const hasLinkedAccount = Boolean(pupil.linked_user_id);
  const connectionState = inviteStatus === "completed" && hasLinkedAccount
    ? { label: "Connected · Active", tone: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" }
    : inviteStatus === "onboarded" || inviteStatus === "joined"
      ? { label: "Setup started · awaiting registration", tone: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" }
      : inviteStatus === "opened"
        ? { label: "Invite opened · not connected", tone: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" }
        : inviteStatus === "invited"
          ? { label: pupil.invite_sent_at ? "Invite sent · not connected" : "Invite pending · not connected", tone: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" }
          : { label: "Not connected", tone: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" };
  const canStartLessonToday = Boolean(
    next_lesson?.date && next_lesson.date === londonYMD(new Date())
  );
  const initials = `${(pupil.first_name || "")[0] || ""}${(pupil.last_name || "")[0] || ""}`.toUpperCase();

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-8">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 py-3 sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800">
            <ChevronLeft className="w-5 h-5 text-slate-500" />
          </button>
          <h1 className="text-lg font-extrabold text-slate-900 flex-1">Pupil Profile</h1>
          <DarkModeToggle />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 mt-5 space-y-5">
        {/* C4c · Instructor→Pupil Progress panel · rendered when
            the URL carries ?tab=progress. Non-invasive: sits above
            the existing profile card. No redesign of the profile. */}
        {activeTab === "progress" && (
          <InstructorProgressPanel pupilId={pupilId} pupilName={pupil.full_name} />
        )}
        {/* Profile card */}
        <div data-testid="pupil-profile-card" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5">
          <div className="flex flex-wrap items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-extrabold text-white flex-shrink-0"
              style={{ backgroundColor: pupil.avatar_color || "#3b82f6" }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-extrabold text-slate-900 truncate">{pupil.full_name}</h2>
              <span
                data-testid="pupil-connection-status"
                className={`inline-block text-xs font-bold px-2.5 py-0.5 rounded-full mt-1 ${connectionState.tone}`}
              >{connectionState.label}</span>
            </div>
            <button data-testid="edit-pupil-btn" onClick={() => setEditing(!editing)}
              className="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700">
              <Pencil className="w-5 h-5 text-slate-400" />
            </button>
            <button
              data-testid="transfer-learner-btn"
              onClick={() => {
                setTransferForm({
                  reason: "better_location",
                  new_postcode: data?.pupil?.postcode || "",
                  note_to_learner: "",
                });
                setTransferModal(true);
              }}
              className="p-2.5 rounded-xl hover:bg-violet-100 dark:hover:bg-violet-900/30"
              title="Transfer Learner"
            >
              <Handshake className="w-5 h-5 text-violet-500" />
            </button>
          </div>

          {/* Contact info */}
          <div className="space-y-2 mb-4">
            {pupil.phone && (
              <div className="flex items-start gap-2.5 text-sm text-slate-600 min-w-0">
                <Phone className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="font-medium min-w-0 break-words whitespace-normal">{pupil.phone}</span>
              </div>
            )}
            {pupil.email && (
              <div className="flex items-start gap-2.5 text-sm text-slate-600 min-w-0">
                <Mail className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="font-medium min-w-0 break-words whitespace-normal">{pupil.email}</span>
              </div>
            )}
            {pupil.pickup_location && (
              <div className="flex items-start gap-2.5 text-sm text-slate-600 min-w-0">
                <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" /><span className="font-medium min-w-0 break-words whitespace-normal">{pupil.pickup_location}</span>
              </div>
            )}
          </div>

          {/* Edit form */}
          {editing && (
            <div data-testid="edit-pupil-form" className="border-t border-slate-100 dark:border-slate-700 pt-4 mt-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 font-bold block mb-1">Phone</label>
                  <input data-testid="edit-phone" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm" /></div>
                <div><label className="text-xs text-slate-400 font-bold block mb-1">Email</label>
                  <input data-testid="edit-email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 font-bold block mb-1">Lesson Price (£)</label>
                  <input data-testid="edit-price" type="number" value={editForm.default_lesson_price} onChange={e => setEditForm({ ...editForm, default_lesson_price: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm" /></div>
                <div><label className="text-xs text-slate-400 font-bold block mb-1">Hours Credit</label>
                  <input data-testid="edit-credit" type="number" step="0.5" value={editForm.hours_credit} onChange={e => setEditForm({ ...editForm, hours_credit: e.target.value })}
                    className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm" /></div>
              </div>
              <div><label className="text-xs text-slate-400 font-bold block mb-1">Permanent pickup and return address</label>
                <textarea
                  data-testid="edit-address-1"
                  value={editForm.address_1}
                  onChange={e => setEditForm({ ...editForm, address_1: e.target.value })}
                  placeholder="Full pickup and return address"
                  rows={2}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm resize-none"
                />
              </div>
              <div><label className="text-xs text-slate-400 font-bold block mb-1">Home Postcode <span className="text-slate-300 font-normal">(UK · territory map)</span></label>
                <input
                  data-testid="edit-home-postcode"
                  value={editForm.home_postcode}
                  onChange={e => setEditForm({ ...editForm, home_postcode: e.target.value })}
                  placeholder="e.g. SW1A 1AA"
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm font-mono uppercase placeholder:normal-case placeholder:text-slate-400"
                />
              </div>
              <div><label className="text-xs text-slate-400 font-bold block mb-1">Notes</label>
                <textarea data-testid="edit-notes" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-3 py-2.5 text-sm h-20" /></div>
              <Button data-testid="save-pupil-btn" onClick={savePupil} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl">Save Changes</Button>
            </div>
          )}

          {pupil.notes && !editing && (
            <p className="text-sm text-slate-500 italic border-t border-slate-100 dark:border-slate-700 pt-3 mt-3">{pupil.notes}</p>
          )}
        </div>

        {/* ADI Operational Continuity Sprint — "Remember This
            Pupil" Phase 1 (Feb 28, 2026). Calm sticky-note style
            instructor-only memory: personality, confidence,
            communication style, what helps them, what overwhelms
            them, continuity reminders. Doctrine: calm memory aid,
            NOT CRM / surveillance / scoring. */}
        <InstructorConversation key={`messages-${pupilId}`} pupilId={pupilId} pupilName={pupil.full_name} />
        <PupilRhythmReviewCard pupilId={pupilId} />

        <PupilMemoryCard pupilId={pupilId} />
        <PupilUnderstandingQueue key={pupilId} pupilId={pupilId} />

        {/* ADI Operational Continuity Sprint — Phase 1
            (Feb 28, 2026). Calm orientation cue: up to 3
            meaningful changes since the instructor last opened
            THIS pupil's profile. Silent on first view and when
            nothing meaningful changed. Doctrine: instructor
            orientation, not an activity feed. */}
        <SinceYouLastOpenedCard pupilId={pupilId} />

        {/* Operational Continuity Convergence V1 (Feb 28, 2026) —
            Break #2: calm "last lesson" chronology line. Provides
            immediate operational grounding when an instructor
            reopens the profile after a gap. Data comes from the
            existing `lessons` array on the same response that
            populates Summary stats below — zero new endpoint.
            Silent when no lessons exist yet. */}
        <PupilLastLessonLine
          lessons={lessons}
          nextLesson={next_lesson}
          nextLessonFocus={pupil?.next_lesson_focus}
        />

        {/* Calm Guidance V1.5 (Feb 28, 2026) — instructor-side
            operational memory tile. Mirrors the learner-side
            struggle rule. Silent unless an active signal exists.
            See /app/backend/routes/instructor_calm_guidance.py. */}
        <StruggleFollowUpTile pupilId={pupilId} />

        {/* Summary stats */}
        <div data-testid="pupil-summary" className="grid grid-cols-2 gap-3">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
            <p className="text-2xl font-extrabold text-blue-600">{summary.total_lessons}</p>
            <p className="text-xs text-slate-400 font-bold mt-0.5">Total Lessons</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
            <p className="text-2xl font-extrabold text-slate-900">{summary.total_hours}h</p>
            <p className="text-xs text-slate-400 font-bold mt-0.5">Total Hours</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
            <p className="text-2xl font-extrabold text-emerald-600">£{summary.total_paid}</p>
            <p className="text-xs text-slate-400 font-bold mt-0.5">Total Paid</p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
            <p className={`text-2xl font-extrabold ${summary.total_unpaid > 0 ? "text-pink-500" : "text-slate-300"}`}>
              £{summary.total_unpaid}
            </p>
            <p className="text-xs text-slate-400 font-bold mt-0.5">Unpaid</p>
          </div>
        </div>

        <PupilFinancialHistory
          endpoint={`/api/lesson-events/pupils/${pupilId}/financial-history`}
          title="Pupil payments & receipts"
        />

        {/* Primary action — only today's scheduled lesson can enter Work Mode. */}
        <button
          data-testid="start-lesson-with-pupil-btn"
          onClick={() => {
            if (canStartLessonToday) nav(`/work-mode?arrive=${pupilId}`);
          }}
          disabled={!canStartLessonToday}
          aria-describedby={!canStartLessonToday ? "start-lesson-date-help" : undefined}
          className={`w-full h-14 rounded-2xl text-white font-extrabold inline-flex items-center justify-center gap-2 transition ${canStartLessonToday
            ? "bg-emerald-600 hover:bg-emerald-700 active:scale-[0.99]"
            : "bg-slate-400 cursor-not-allowed"
          }`}
        >
          <Play className="w-5 h-5 fill-current" />
          {canStartLessonToday
            ? `Start lesson with ${pupil.first_name || "this pupil"}`
            : "Starts on lesson day"}
        </button>
        {!canStartLessonToday && next_lesson && (
          <p id="start-lesson-date-help" className="text-center text-xs text-slate-500 dark:text-slate-400 -mt-2">
            Next lesson: {new Date(next_lesson.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} at {next_lesson.start_time}
          </p>
        )}

        {/* Credit */}
        {summary.hours_credit > 0 && (
          <div data-testid="credit-display" className="bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-800 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-bold text-violet-700 dark:text-violet-400">Hours Credit</p>
              <p className="text-xs text-violet-500 mt-0.5">Pre-paid lesson hours remaining</p>
            </div>
            <p className="text-3xl font-extrabold text-violet-600 dark:text-violet-400">{summary.hours_credit}h</p>
          </div>
        )}

        {/* Next lesson */}
        {next_lesson && (
          <div data-testid="next-lesson" className="bg-orange-50 dark:bg-orange-900/15 border border-orange-200 dark:border-orange-800 rounded-2xl p-4">
            <p className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-1">Next Lesson</p>
            <p className="text-base font-bold text-slate-900">
              {new Date(next_lesson.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} at {next_lesson.start_time}
            </p>
            {next_lesson.pickup_location && (
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" />{next_lesson.pickup_location}</p>
            )}
          </div>
        )}

        {next_lesson && lesson_readiness?.current && (
          <div
            data-testid="next-lesson-readiness"
            className="bg-blue-50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800 rounded-2xl p-4 space-y-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">
                  Preparation for this lesson
                </p>
                <p className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5">
                  {String(lesson_readiness.current.status || "not_enough_information").replaceAll("_", " ")}
                </p>
              </div>
              <span className="text-xs font-bold rounded-full px-2.5 py-1 bg-white dark:bg-slate-800 text-amber-700">
                {String(next_lesson.payment_status || "unpaid").replaceAll("_", " ")}
                {Number(next_lesson.amount || 0) > 0
                  ? ` · £${Number(next_lesson.amount).toFixed(2)}`
                  : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p className="rounded-xl bg-white/80 dark:bg-slate-800 p-2.5">
                <span className="block text-[10px] font-bold uppercase text-slate-400">Homework</span>
                {String(lesson_readiness.current.preparation || "not_recorded").replaceAll("_", " ")}
              </p>
              <p className="rounded-xl bg-white/80 dark:bg-slate-800 p-2.5">
                <span className="block text-[10px] font-bold uppercase text-slate-400">Understanding</span>
                {String(lesson_readiness.current.understanding || "not_checked").replaceAll("_", " ")}
              </p>
            </div>
            {lesson_readiness.next_best?.instructor_next_step && (
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-bold">Next move: </span>
                {lesson_readiness.next_best.instructor_next_step}
              </p>
            )}
            {lesson_readiness.next_best?.next_question && (
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-bold">Ask: </span>
                {lesson_readiness.next_best.next_question}
              </p>
            )}
            {lesson_readiness.next_best?.proof_needed && (
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <span className="font-bold">Proof to seek: </span>
                {lesson_readiness.next_best.proof_needed}
              </p>
            )}
            <p className="text-[11px] text-slate-500">
              Pupil preparation is evidence, not a practical pass. Confirm the driving outcome in the lesson.
            </p>
          </div>
        )}

        {/* AI Lesson Review — same surface as Work Mode, pupil pre-filled.
            Use this for retrospective uploads outside an active lesson. */}
        <VideoEvidenceReview key={`video-evidence-${pupilId}`} pupilId={pupilId} />
        <WorkModeAIReviewSection
          pupilId={pupilId}
          pupilName={`${pupil.first_name || ""} ${pupil.last_name || ""}`.trim() || "Pupil"}
          isAdmin={user?.role === "admin"}
        />

        {/* Stage confirmation — Phase 1 */}
        <div data-testid="stage-confirmation-card" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                Stage confirmation
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                Confirm where {pupil.first_name || "this pupil"} is on a stage. Ready unlocks the next stage and issues a Passport cert. Nearly there or Not yet creates a positive carry-forward for the next lesson.
              </p>
            </div>
            <button
              data-testid="stage-confirmation-open-btn"
              onClick={() => setStageConfOpen(true)}
              className="h-9 rounded-xl px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-extrabold flex-none"
            >
              Confirm a stage
            </button>
          </div>
          {stageConfirmations.length > 0 && (
            <ul data-testid="stage-confirmations-recent" className="space-y-1.5">
              {stageConfirmations.slice(0, 4).map((c) => {
                const tone =
                  c.verdict === "ready"
                    ? "bg-emerald-100 text-emerald-700"
                    : c.verdict === "nearly_there"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-700";
                const label =
                  c.verdict === "ready" ? "Ready"
                  : c.verdict === "nearly_there" ? "Nearly there"
                  : "Not yet";
                return (
                  <li
                    key={c.id}
                    data-testid={`stage-confirmation-row-${c.stage_slug}`}
                    className="flex items-center gap-2 text-[12px]"
                  >
                    <span className={`inline-block px-2 py-0.5 rounded-full font-extrabold ${tone}`}>
                      {label}
                    </span>
                    <span className="font-bold text-slate-700 dark:text-slate-200 flex-1 min-w-0 truncate">
                      {c.stage_label}
                    </span>
                    <span className="text-[10px] text-slate-400 flex-none">
                      {c.confirmed_at?.slice(0, 10)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Lesson history */}
        <div>
          <h3 className="text-lg font-extrabold text-slate-900 mb-3">Lesson History</h3>
          {lessons.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No lessons yet</p>
          ) : (
            <div className="space-y-2">
              {lessons.map(le => {
                const psCfg = PAYMENT_BADGE[paymentBadgeKey(le)];
                const PayIcon = psCfg?.icon;
                const isCancelled = isRemovedFromActiveDiary(le);
                return (
                  <button
                    type="button"
                    key={le.id}
                    data-testid={`history-lesson-${le.id}`}
                    onClick={() => setSelectedLesson(le)}
                    aria-label={`Open lesson details for ${le.date} at ${le.start_time}`}
                    className={`w-full text-left bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 active:scale-[0.99] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${isCancelled ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-bold text-slate-900">
                          {new Date(le.date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        <span className="text-sm text-slate-400">{le.start_time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {le.amount != null && <span className="text-sm font-bold text-slate-700">£{le.amount}</span>}
                        {psCfg && (
                          <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${psCfg.bg}`}>
                            {PayIcon && <PayIcon className="w-2.5 h-2.5" />}{psCfg.text}
                          </span>
                        )}
                        {isCancelled && (
                          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-slate-400">
                            <X className="w-2.5 h-2.5" />Cancelled
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                      </div>
                    </div>
                    {(le.pickup_location || le.notes) && (
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                        {le.pickup_location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{le.pickup_location}</span>}
                        {le.notes && <span className="truncate">{le.notes}</span>}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <LessonHistoryDetail
        lesson={selectedLesson}
        onClose={() => setSelectedLesson(null)}
      />

      {/* Transfer Learner modal (Phase 7) */}
      {transferModal && (
        <MobileDialog
          bareContent
          open={!!transferModal}
          onClose={() => setTransferModal(false)}
          testid="transfer-learner-modal-wrap"
        >
          <div
            data-testid="transfer-learner-modal"
            className="bg-white dark:bg-slate-800 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl"
          >
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2">
              <Handshake className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-bold text-slate-900 dark:text-white flex-1">
                Transfer Learner
              </h3>
              <button
                type="button"
                onClick={() => setTransferModal(false)}
                data-testid="transfer-learner-close"
                className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-violet-50 dark:bg-violet-900/15 border border-violet-200 dark:border-violet-800 rounded-xl p-3">
                <p className="text-xs text-violet-900 dark:text-violet-200 leading-relaxed">
                  Suggest a new local match for{" "}
                  <span className="font-bold">{data?.pupil?.full_name}</span>. They must
                  approve before anything happens — their progress and confidence carry
                  forward through the Transfer Network.
                </p>
              </div>

              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Reason
                <select
                  value={transferForm.reason}
                  onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                  data-testid="transfer-learner-reason"
                  className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                >
                  <option value="better_location">Better local fit available</option>
                  <option value="moved_area">They've moved area</option>
                  <option value="instructor_unavailable">I don't have availability</option>
                  <option value="transmission_change">Transmission mismatch</option>
                  <option value="different_style">Different style would suit them</option>
                  <option value="instructor_initiated">General suggestion</option>
                </select>
              </label>

              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Their postcode (defaults to the one on record)
                <input
                  type="text"
                  value={transferForm.new_postcode}
                  onChange={(e) =>
                    setTransferForm({ ...transferForm, new_postcode: e.target.value.toUpperCase() })
                  }
                  placeholder="e.g. M1 2AB"
                  maxLength={12}
                  data-testid="transfer-learner-postcode"
                  className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                />
              </label>

              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Quick note for the learner (optional)
                <textarea
                  value={transferForm.note_to_learner}
                  onChange={(e) =>
                    setTransferForm({ ...transferForm, note_to_learner: e.target.value })
                  }
                  maxLength={500}
                  rows={3}
                  placeholder="Something reassuring — why this change would help them"
                  data-testid="transfer-learner-note"
                  className="w-full mt-1 text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 resize-none"
                />
              </label>

              <button
                type="button"
                onClick={submitTransfer}
                disabled={transferring}
                data-testid="transfer-learner-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-violet-600 text-white font-bold text-sm rounded-xl px-4 py-3 hover:bg-violet-700 disabled:bg-violet-300"
              >
                <Handshake className="w-4 h-4" />
                {transferring ? "Sending…" : "Send suggestion to learner"}
              </button>
              <p className="text-[11px] text-slate-400 text-center">
                The learner has full control — they can approve or decline.
              </p>
            </div>
          </div>
        </MobileDialog>
      )}

      <StageConfirmationModal
        open={stageConfOpen}
        onClose={() => setStageConfOpen(false)}
        pupilId={pupilId}
        pupilFirstName={pupil?.first_name}
        onConfirmed={() => fetchStageConfirmations()}
      />
    </div>
  );
}


function _lessonValues(value) {
  if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
  if (value == null) return [];
  const cleaned = String(value).trim();
  return cleaned ? [cleaned] : [];
}

function LessonHistoryDetail({ lesson, onClose }) {
  if (!lesson) return null;

  const wentWell = _lessonValues(lesson.went_well);
  const needsWork = _lessonValues(lesson.needs_work);
  const pupilFeedback = lesson.pupil_feedback || {};
  const nextFocus = _lessonValues(
    lesson.next_lesson_focus?.length
      ? lesson.next_lesson_focus
      : pupilFeedback.suggested_focus,
  );
  const homeworkAssignments = Array.isArray(lesson.homework_assignments)
    ? lesson.homework_assignments
    : [];
  const hasDebrief = Boolean(
    wentWell.length
    || needsWork.length
    || lesson.instructor_notes
    || pupilFeedback.summary
    || pupilFeedback.behavior_link
    || pupilFeedback.confidence_reinforcement
    || nextFocus.length
    || homeworkAssignments.length
  );
  const isReleased = Boolean(lesson.released || lesson.status === "released");

  return (
    <MobileDialog
      bareContent
      open={!!lesson}
      onClose={onClose}
      testid="lesson-history-detail-wrap"
    >
      <div
        data-testid="lesson-history-detail"
        className="bg-white dark:bg-slate-800 w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85dvh] overflow-y-auto"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 sticky top-0 bg-white dark:bg-slate-800">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
              Lesson details
            </h3>
            <p className="text-xs text-slate-500">
              {lesson.date} · {lesson.start_time || "Time not recorded"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="lesson-history-detail-close"
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label="Close lesson details"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div
            data-testid="lesson-history-release-state"
            className={`rounded-xl px-3 py-2 text-sm font-bold ${
              isReleased
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {isReleased ? "Released to pupil" : "Not released to pupil"}
          </div>

          {!hasDebrief ? (
            <p className="text-sm text-slate-500">No debrief was recorded for this lesson.</p>
          ) : (
            <>
              {(pupilFeedback.summary || pupilFeedback.behavior_link || pupilFeedback.confidence_reinforcement) && (
                <section data-testid="lesson-history-pupil-feedback" className="rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/15">
                  <h4 className="text-sm font-extrabold text-blue-900 dark:text-blue-200">What the pupil receives</h4>
                  {pupilFeedback.summary && <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">{pupilFeedback.summary}</p>}
                  {pupilFeedback.behavior_link && <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">{pupilFeedback.behavior_link}</p>}
                  {pupilFeedback.confidence_reinforcement && <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">{pupilFeedback.confidence_reinforcement}</p>}
                </section>
              )}

              {wentWell.length > 0 && (
                <section>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-emerald-700">Went well</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">{wentWell.join(", ")}</p>
                </section>
              )}

              {needsWork.length > 0 && (
                <section>
                  <h4 className="text-xs font-bold uppercase tracking-wide text-amber-700">Needs work</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">{needsWork.join(", ")}</p>
                </section>
              )}

              {nextFocus.length > 0 && (
                <section data-testid="lesson-history-next-focus">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-blue-700">Next lesson focus</h4>
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-1">{nextFocus.join(", ")}</p>
                </section>
              )}

              {homeworkAssignments.length > 0 && (
                <section
                  data-testid="lesson-history-homework-delivery"
                  className="rounded-xl border border-violet-200 bg-violet-50 p-3 dark:border-violet-800 dark:bg-violet-900/15"
                >
                  <h4 className="text-xs font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                    Homework issued
                  </h4>
                  <div className="mt-2 space-y-2">
                    {homeworkAssignments.map((item) => (
                      <div key={item.id || item.lesson_title}>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                          {item.lesson_title || item.skill_id || "Learning preparation"}
                          <span className="ml-2 text-xs font-semibold text-violet-700 dark:text-violet-300">
                            {item.completed_at ? "Completed" : "Assigned"}
                          </span>
                        </p>
                        {item.reason && (
                          <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                            {item.reason}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {lesson.instructor_notes && (
                <section className="border-t border-slate-200 dark:border-slate-700 pt-3">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500">Private instructor notes</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap">{lesson.instructor_notes}</p>
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </MobileDialog>
  );
}


function _humaniseLessonGap(iso) {
  if (!iso) return null;
  try {
    const then = new Date(iso);
    const ms = Date.now() - then.getTime();
    const days = Math.floor(ms / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks === 1) return "a week ago";
    if (weeks < 5) return `${weeks} weeks ago`;
    const months = Math.floor(days / 30);
    if (months === 1) return "a month ago";
    if (months < 12) return `${months} months ago`;
    return "over a year ago";
  } catch {
    return null;
  }
}


function PupilLastLessonLine({ lessons, nextLesson, nextLessonFocus }) {
  // Pull the most recent released/completed lesson from the
  // existing `lessons` array on the same fetched response. Calm,
  // single line, non-analytic — no `%`, no scores, no risk.
  if (!Array.isArray(lessons) || lessons.length === 0) {
    return null;
  }
  // Lessons are typically already most-recent-first; defensive sort.
  const sorted = [...lessons].sort((a, b) => {
    const ad = new Date(a.ended_at || a.lesson_date || a.created_at || 0).getTime();
    const bd = new Date(b.ended_at || b.lesson_date || b.created_at || 0).getTime();
    return bd - ad;
  });
  const last = sorted[0];
  const when = _humaniseLessonGap(
    last.ended_at || last.lesson_date || last.created_at,
  );
  if (!when) return null;
  const focus = (last.focus_area || last.topic || last.focus || "").trim();

  const next = nextLesson?.lesson_date || nextLesson?.start_at || nextLesson?.date;
  const nextWhen = next ? new Date(
    typeof next === "string" && next.length === 10 ? `${next}T00:00:00` : next,
  ).toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short",
  }) : null;

  // ADI L3.1 continuity batch (Feb 28, 2026) — Break #1: surface
  // the instructor's teed-up next-lesson focus inline. Accepts
  // either an array (work_students.next_lesson_focus) or a single
  // string. Silent when missing / empty / whitespace-only.
  const nextFocusLabel = (() => {
    if (!nextLessonFocus) return "";
    if (Array.isArray(nextLessonFocus)) {
      const cleaned = nextLessonFocus.map(s => String(s).trim()).filter(Boolean);
      return cleaned.slice(0, 2).join(", ");
    }
    return String(nextLessonFocus).trim();
  })();

  return (
    <p
      data-testid="pupil-last-lesson-line"
      className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed"
    >
      <span className="font-bold text-slate-600 dark:text-slate-300">
        Last lesson:
      </span>{" "}
      {focus ? `${focus} · ` : ""}{when}
      {nextWhen && (
        <>
          {" · "}
          <span className="font-bold text-slate-600 dark:text-slate-300">
            Next:
          </span>{" "}
          {nextWhen}
        </>
      )}
      {nextFocusLabel && (
        <>
          {" · "}
          <span
            data-testid="pupil-next-focus-inline"
            className="font-bold text-slate-600 dark:text-slate-300"
          >
            Next focus:
          </span>{" "}
          {nextFocusLabel}
        </>
      )}
    </p>
  );
}


// ═══════════════════════════════════════════════════════════════════
// C4c · Instructor→Pupil Progress panel · Aug 2026
// ═══════════════════════════════════════════════════════════════════
// Reads the summarised instructor-context view from the canonical
// progress engine. Ownership is enforced server-side on the endpoint
// (a direct URL bypass with an unauthorised pupil_id returns 403).
//
// Separates OBSERVED evidence from INTERPRETATION by rendering
// per-bucket lists AND showing the engine's own rationale + evidence
// count for each dimension. Buckets:
//   • Secure
//   • Developing
//   • Needs work
//   • Insufficient evidence — the SoT rationale for "cannot yet
//     determine" so absence-of-evidence is never turned into
//     evidence-of-failure.

const _BUCKET_LABEL = {
  secure:                "Appears secure",
  developing:            "Still developing",
  needs_work:            "Needs teaching / practice",
  insufficient_evidence: "Not enough evidence yet",
};

function InstructorProgressPanel({ pupilId, pupilName }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    axios.get(`${API}/api/pupils/${pupilId}/instructor-progress`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => {
        if (cancelled) return;
        setError(
          e?.response?.status === 403
            ? "You aren't authorised to view this pupil's progress."
            : "Couldn't load progress just now."
        );
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pupilId]);

  return (
    <div
      data-testid="pupil-progress-panel"
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
          Progress · {pupilName || "pupil"}
        </h3>
        {data?.coverage && (
          <span
            data-testid="pupil-progress-coverage"
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
          >
            {data.coverage} evidence
          </span>
        )}
      </div>

      {loading && <p className="text-sm text-slate-500">Reading recent evidence…</p>}
      {error && <p data-testid="pupil-progress-error" className="text-sm text-rose-600">{error}</p>}

      {data && !loading && !error && (
        <div className="space-y-3">
          {Array.isArray(data.caveats) && data.caveats.length > 0 && (
            <p
              data-testid="pupil-progress-caveat"
              className="text-xs italic text-slate-500 dark:text-slate-400"
            >
              {data.caveats.join(" · ")}
            </p>
          )}
          {["secure", "developing", "needs_work", "insufficient_evidence"].map((b) => {
            const items = (data.buckets || {})[b] || [];
            return (
              <div key={b} data-testid={`pupil-progress-bucket-${b}`}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600 mb-1">
                  {_BUCKET_LABEL[b]} · {items.length}
                </p>
                {items.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500">— None recorded yet.</p>
                ) : (
                  <ul className="space-y-1">
                    {items.map((it) => (
                      <li
                        key={`${b}-${it.dimension}`}
                        data-testid={`pupil-progress-dim-${it.dimension}`}
                        className="text-sm text-slate-700 dark:text-slate-200"
                      >
                        <span className="font-semibold">{it.dimension.replace(/_/g, " ")}</span>
                        {typeof it.evidence_count === "number" && (
                          <span className="ml-1 text-xs text-slate-400">
                            · {it.evidence_count} evidence
                          </span>
                        )}
                        {it.rationale && (
                          <span className="block text-xs italic text-slate-500 dark:text-slate-400 mt-0.5">
                            {it.rationale}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          <p className="text-[10px] text-slate-400 pt-1">
            Observed evidence — interpretation is provisional. Where the platform
            cannot determine something reliably, it is placed in "not enough
            evidence yet" rather than treated as a failure.
          </p>

          {Array.isArray(data.teaching_support) && data.teaching_support.length > 0 && (() => {
            // C4e · Instructor Progress Insight · Aug 2026.
            // Show ONE thing only — the first deterministic teaching-
            // support item. Reuses the same observed/hypothesis fields
            // returned by the canonical endpoint; opens the Knowledge
            // Package through the canonical /help?q=<skill_id> route.
            // If teaching_support is empty, this branch does not render.
            const ts = data.teaching_support[0];
            return (
              <div
                data-testid="pupil-teaching-support"
                className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-1.5"
              >
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600">
                  Worth exploring next
                </p>
                <div
                  data-testid={`pupil-teaching-support-${ts.subject}`}
                  className="text-sm"
                >
                  <p className="font-semibold text-slate-800 dark:text-slate-100">
                    {ts.title}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    <span className="font-semibold">Observed:</span> {ts.observed}
                  </p>
                  <p className="text-xs italic text-slate-500 dark:text-slate-400">
                    <span className="font-semibold">Possible understanding gap:</span> {ts.hypothesis}
                  </p>
                  <a
                    data-testid="pupil-teaching-support-open"
                    href={`/help?q=${encodeURIComponent(ts.subject)}`}
                    className="inline-block mt-1 text-xs font-semibold text-blue-700 hover:underline"
                  >
                    Teaching support →
                  </a>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
